import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CBadge, CCard, CPagination } from "@coreui/react-pro";
import type {
  TrackerOpenTradeDto,
  TrackerStrategyDto,
  TrackerStrategyResultDto,
  TrackerTradeDto,
} from "../../../api/hooks/usePortfolioTracker";
import { displayTicker } from "../../trading-strategies/tickers";
import {
  TRADES_PER_PAGE,
  daysBetween,
  formatDay,
  formatMoney,
  formatPercent,
  formatPrice,
  formatSignedMoney,
  formatSignedPercent,
  toneOf,
} from "../portfolio-tracker";

interface TrackerTradesProps {
  strategy: TrackerStrategyDto;
  result: TrackerStrategyResultDto;
  asOf: string;
}

type Row = { open: TrackerOpenTradeDto } | { closed: TrackerTradeDto };

/** A strategy's trades from its start, the open one first, then newest exit first, ten to a page. */
export function TrackerTrades({ strategy, result, asOf }: TrackerTradesProps) {
  const { t } = useTranslation("portfolioTracker");
  const [page, setPage] = useState(1);

  const rows: Row[] = [
    ...(result.openTrade ? [{ open: result.openTrade }] : []),
    ...result.trades.map((closed) => ({ closed })),
  ];
  const pages = Math.max(1, Math.ceil(rows.length / TRADES_PER_PAGE));
  const current = Math.min(page, pages);
  const shown = rows.slice((current - 1) * TRADES_PER_PAGE, current * TRADES_PER_PAGE);
  const alertPriced =
    result.trades.some((trade) => trade.buyPricedAtAlert || trade.exitPricedAtAlert) ||
    result.openTrade?.buyPricedAtAlert === true;

  const ticker = (symbol: string) => (
    <>
      <span className="tracker-ticker is-small">{displayTicker(symbol)}</span>
      {strategy.bearSymbol && displayTicker(symbol) === displayTicker(strategy.bearSymbol) && (
        <span className="tracker-short-side">{t("trades.shortSide")}</span>
      )}
    </>
  );

  const price = (value: number | null, atAlert: boolean) =>
    value === null ? (
      <>
        <span className="text-body-secondary">—</span>
        <small>{t("trades.noPrice")}</small>
      </>
    ) : (
      <>
        {formatPrice(value)}
        {atAlert && <small>{t("trades.alertPrice")}</small>}
      </>
    );

  const amount = (trade: { amount: number | null; balanceAtBuy: number | null; reduced?: boolean }) =>
    trade.amount === null || trade.balanceAtBuy === null ? (
      <span className="text-body-secondary">—</span>
    ) : trade.amount === 0 ? (
      <>
        <span className="text-body-secondary">—</span>
        <small>{t("trades.notTaken")}</small>
      </>
    ) : (
      <>
        {formatMoney(trade.amount)}
        <small>
          {trade.reduced
            ? t("trades.cut")
            : t("trades.ofBalance", {
                percent: formatPercent((trade.amount / trade.balanceAtBuy) * 100),
                balance: formatMoney(trade.balanceAtBuy),
              })}
        </small>
      </>
    );

  return (
    <CCard className="tracker-trades">
      <div className="tracker-trades-head">
        <h3 className="h6 mb-0">{t("trades.title")}</h3>
        <p className="mb-0">
          {t("trades.description", { percent: formatPercent(result.allocationPercent) })}
          {alertPriced && ` ${t("trades.alertPriceNote")}`}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="tracker-trades-note">{t("trades.none", { date: formatDay(result.start) })}</p>
      ) : (
        <div className="tracker-trades-scroll">
          <table className="tracker-trades-table">
            <thead>
              <tr>
                <th>{t("trades.bought")}</th>
                <th className="text-end">{t("trades.price")}</th>
                <th className="text-end">{t("trades.amount")}</th>
                <th>{t("trades.exited")}</th>
                <th className="text-end">{t("trades.price")}</th>
                <th className="text-end">{t("trades.held")}</th>
                <th className="text-end">{t("trades.return")}</th>
                <th className="text-end">{t("trades.profit")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                if ("open" in row) {
                  const open = row.open;
                  return (
                    <tr key={`open-${open.buyDate}`}>
                      <td>
                        {ticker(open.symbol)}
                        {formatDay(open.buyDate)}
                      </td>
                      <td className="text-end">{price(open.buyPrice, open.buyPricedAtAlert)}</td>
                      <td className="text-end">{amount(open)}</td>
                      <td>
                        <CBadge color="warning" shape="rounded-pill">
                          {t("trades.open")}
                        </CBadge>
                      </td>
                      <td className="text-end text-body-secondary">—</td>
                      <td className="text-end text-body-secondary">
                        {t("trades.daysSoFar", { count: daysBetween(open.buyDate, asOf) })}
                      </td>
                      <td className="text-end text-body-secondary" colSpan={2}>
                        {t("trades.countsWhenClosed")}
                      </td>
                    </tr>
                  );
                }

                const trade = row.closed;
                const held = daysBetween(trade.buyDate, trade.exitDate);
                return (
                  <tr key={`${trade.buyDate}-${trade.exitDate}-${trade.symbol}`}>
                    <td>
                      {ticker(trade.symbol)}
                      {formatDay(trade.buyDate)}
                    </td>
                    <td className="text-end">{price(trade.buyPrice, trade.buyPricedAtAlert)}</td>
                    <td className="text-end">{amount(trade)}</td>
                    <td>{formatDay(trade.exitDate)}</td>
                    <td className="text-end">{price(trade.exitPrice, trade.exitPricedAtAlert)}</td>
                    <td className="text-end">{t("trades.days", { count: held })}</td>
                    {trade.returnPercent === null || trade.profit === null ? (
                      <td className="text-end" colSpan={2}>
                        <CBadge color="secondary" shape="rounded-pill">
                          {t("trades.noPrice")}
                        </CBadge>
                        <small>{t("trades.leftOut")}</small>
                      </td>
                    ) : (
                      <>
                        <td className={`text-end ${toneOf(trade.returnPercent * 10)}`}>
                          {formatSignedPercent(trade.returnPercent)}
                        </td>
                        {trade.amount === 0 ? (
                          <td className="text-end text-body-secondary">—</td>
                        ) : (
                          <td className={`text-end ${toneOf(trade.profit)}`}>{formatSignedMoney(trade.profit)}</td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {result.exitBeforeStart && (
        <p className="tracker-trades-note">
          {t("trades.before", {
            name: strategy.name,
            symbol: displayTicker(result.exitBeforeStart.symbol),
            date: formatDay(result.exitBeforeStart.buyDate),
          })}
        </p>
      )}

      <Pager total={rows.length} page={current} onPage={setPage} />
    </CCard>
  );
}

/** "Showing 11–20 of 58", and the pages: the first, the last, and those beside this one. */
function Pager({ total, page, onPage }: { total: number; page: number; onPage: (page: number) => void }) {
  const { t } = useTranslation("portfolioTracker");
  const pages = Math.max(1, Math.ceil(total / TRADES_PER_PAGE));
  const from = total ? (page - 1) * TRADES_PER_PAGE + 1 : 0;
  const to = Math.min(total, page * TRADES_PER_PAGE);
  const numbers = [...new Set([1, pages, page - 1, page, page + 1])]
    .filter((number) => number >= 1 && number <= pages)
    .sort((a, b) => a - b);

  const item = (label: string, target: number, options: { disabled?: boolean; current?: boolean; name?: string }) => (
    <li
      key={options.name ?? label}
      className={`page-item${options.current ? " active" : ""}${options.disabled ? " disabled" : ""}`}
    >
      <button
        type="button"
        className="page-link"
        disabled={options.disabled}
        aria-current={options.current ? "page" : undefined}
        aria-label={options.name}
        onClick={() => onPage(target)}
      >
        {label}
      </button>
    </li>
  );

  return (
    <div className="tracker-trades-foot">
      <span>{t("trades.showing", { from, to, total })}</span>
      {pages > 1 && (
        <CPagination size="sm" className="mb-0" aria-label={t("trades.pages")}>
          {item("‹", page - 1, { disabled: page === 1, name: t("trades.previous") })}
          {numbers.flatMap((number, index) => [
            ...(index > 0 && number - numbers[index - 1] > 1
              ? [
                  <li key={`gap-${number}`} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>,
                ]
              : []),
            item(String(number), number, { current: number === page }),
          ])}
          {item("›", page + 1, { disabled: page === pages, name: t("trades.next") })}
        </CPagination>
      )}
    </div>
  );
}
